import React, { useState } from "react";
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Tooltip,
  IconButton,
} from "@mui/material";
import { useAuth } from "../context/AuthContext";

const ReservationHistory = ({
  reservations = [],
  setView,
  setReservationToShow,
}) => {
  const uniqueReservations = reservations.filter(
    (res, index, self) =>
      index === self.findIndex((r) => r.reservationId === res.reservationId)
  );
  const [filteredReservations, setFilteredReservations] =
    useState(uniqueReservations);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("all");
  const { user } = useAuth();
  const rooms = user?.rooms || [];

  const parseCurrency = (value) => {
    if (typeof value === "string") {
      return parseFloat(value.replace(/[^0-9.-]+/g, "")) || 0;
    } else if (typeof value === "number") {
      return value;
    }
    return 0;
  };
  const getBackgroundColor = (estado, diferencia, seña) => {
    const colores = {
      enProceso: "rgba(64, 224, 208, 0.5)", // turquesa suave
      noAbonado: "rgba(255, 8, 0, 0.55)", // rojo claro
      parcialmentePagado: "rgba(255, 224, 102, 0.58)", // amarillo pastel
      pagado: "rgba(0, 255, 0, 0.49)", // verde claro
      sobrepagado: "rgba(0, 255, 21, 0.53)", // celeste pastel
      default: "transparent",
    };

    switch (true) {
      case estado === "processing":
        return colores.enProceso;
      case seña === 0:
        return colores.noAbonado;
      case diferencia < 0:
        return colores.sobrepagado;
      case diferencia > 0:
        return colores.parcialmentePagado;
      case diferencia === 0:
        return colores.pagado;
      default:
        return colores.default;
    }
  };

  const formatDate = (date) => {
    try {
      const d = new Date(date);
      const day = String(d.getUTCDate()).padStart(2, "0");
      const month = String(d.getUTCMonth() + 1).padStart(2, "0");
      const year = d.getUTCFullYear();
      return `${day}-${month}-${year}`;
    } catch {
      return "Fecha inválida";
    }
  };

  const filterReservations = (res) => {
    const fullName = res.fullName?.toLowerCase() || "";
    const seña = parseCurrency(res["seña"]);
    const total = parseCurrency(res["amount"]);
    const diferencia = total - seña;
    const matchesSearch = fullName.includes(searchTerm.toLowerCase());

    if (!matchesSearch) return false;

    if (filter === "paid") return diferencia === 0;
    if (filter === "owed") return diferencia > 0;
    if (filter === "unpaid") return seña === 0 || seña === "none";
    return true;
  };

  const getRoomNamesByReservationId = (reservationId) => {
    const matching = reservations.filter(
      (r) => r.reservationId === reservationId
    );

    const nombres = matching.map((res) => {
      const unitId = String(res.unitId || "");
      const roomId = String(res.roomId || "");

      let room = null;

      if (unitId) {
        room = rooms.find(
          (r) => String(r.unitId) === unitId && String(r.roomId) === roomId
        );
      }

      if (!room && roomId) {
        room = rooms.find((r) => String(r.roomId) === roomId);
      }

      if (room) {
        const tipo =
          room.type === "private" ? "P" : room.type === "shared" ? "C" : "";
        return `${room.name.toUpperCase()} (${tipo})`;
      }

      return `[roomId: ${roomId} | unitId: ${unitId}]`;
    });

    return [...new Set(nombres)].join(", ");
  };

  const calculateNights = (start, end) => {
    try {
      const startDate = new Date(start);
      const endDate = new Date(end);
      const diffTime = endDate - startDate;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays || 1;
    } catch {
      return 1;
    }
  };
  const columnHeaders = [
    "Nombre",
    "In",
    "Out",
    "Room",
    "H", // 👤 Huéspedes
    "Noches",
    "Abonado",
    "Total",
    "Diferencia",
    "Fuente",
    "Tax OTA",
    "Tax Ubika",
    "Última Mod.",
  ];

  const handleClick = (res) => {
    setReservationToShow(res.reservationId);
    setView("reserva_unica");
  };

  return (
    <Box
      sx={{
        bgcolor: "rgba(144, 58, 242, 0.15)", // Violeta Ubika con transparencia
      }}
    >
      <Box
        p={2}
        display="flex"
        justifyContent="space-between"
        flexWrap="wrap"
        alignItems="center"
        gap={2}
        borderRadius={2}
        boxShadow="0 2px 6px rgba(0,0,0,0.05)"
      >
        <TextField
          label="Buscar por nombre"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ minWidth: { xs: "100%", sm: 240 } }}
        />

        <FormControl
          variant="outlined"
          size="small"
          sx={{ minWidth: { xs: "100%", sm: 200 } }}
        >
          <InputLabel>Estado</InputLabel>
          <Select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            label="Estado"
          >
            <MenuItem value="all">Todos</MenuItem>
            <MenuItem value="paid">Pagado</MenuItem>
            <MenuItem value="owed">Falta pagar</MenuItem>
            <MenuItem value="unpaid">No pagó</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Paper
        elevation={3}
        sx={{
          maxHeight: "calc(100vh - 140px)",
          overflowY: "auto",
          bgcolor: "rgba(144, 58, 242, 0.15)", // Violeta Ubika con transparencia
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              {columnHeaders.map((title) => (
                <TableCell
                  key={title}
                  align={
                    [
                      "H",
                      "Noches",
                      "Abonado",
                      "Total",
                      "Diferencia",
                      "Tax OTA",
                      "Tax Ubika",
                    ].includes(title)
                      ? "right"
                      : "left"
                  }
                  sx={{
                    fontWeight: "bold",
                    fontFamily: "'Red Hat Display'",
                    textTransform: "uppercase",
                    fontSize: "0.75rem",
                    paddingY: 1,
                    color: "#333",
                    borderBottom: "2px solid #ccc",
                  }}
                >
                  {title === "Abonado" ? (
                    <Box display="inline-flex" alignItems="center" gap={0.5}>
                      Abonado
                      <Tooltip
                        title={
                          <Box>
                            <Typography fontSize="0.75rem" mb={1}>
                              Monto abonado hasta el momento
                            </Typography>
                            <Box
                              display="flex"
                              flexDirection="column"
                              gap={0.5}
                            >
                              <Box display="flex" alignItems="center" gap={1}>
                                <Box
                                  width={12}
                                  height={12}
                                  borderRadius="50%"
                                  bgcolor="rgb(255, 8, 0)"
                                />
                                <Typography fontSize="0.7rem">
                                  No abonó
                                </Typography>
                              </Box>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Box
                                  width={12}
                                  height={12}
                                  borderRadius="50%"
                                  bgcolor="rgb(255, 224, 102)"
                                />
                                <Typography fontSize="0.7rem">
                                  Abono parcial
                                </Typography>
                              </Box>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Box
                                  width={12}
                                  height={12}
                                  borderRadius="50%"
                                  bgcolor="rgb(112, 219, 112)"
                                />
                                <Typography fontSize="0.7rem">
                                  Pagado
                                </Typography>
                              </Box>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Box
                                  width={12}
                                  height={12}
                                  borderRadius="50%"
                                  bgcolor="rgb(112, 219, 112)"
                                />
                                <Typography fontSize="0.7rem">
                                  Sobrepago
                                </Typography>
                              </Box>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Box
                                  width={12}
                                  height={12}
                                  borderRadius="50%"
                                  bgcolor="rgb(64, 224, 208)"
                                />
                                <Typography fontSize="0.7rem">
                                  No Valida
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        }
                        arrow
                        placement="top"
                        componentsProps={{
                          tooltip: {
                            sx: {
                              color: "#fff",
                              fontSize: "0.85rem",
                              backgroundColor: "#333",
                              borderRadius: "8px",
                              padding: "1vh",
                            },
                          },
                        }}
                      >
                        <IconButton
                          size="small"
                          sx={{
                            backgroundColor: "#903AF2",
                            color: "#fff",
                            width: "20px",
                            height: "20px",
                            padding: 0,
                            "&:hover": {
                              backgroundColor: "#6e2bbf",
                            },
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: "bold",
                              lineHeight: 1,
                            }}
                          >
                            ?
                          </Typography>
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ) : (
                    title
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredReservations.filter(filterReservations).length > 0 ? (
              [...filteredReservations]
                .filter(filterReservations)
                .reverse()
                .map((res, idx) => {
                  const seña = parseCurrency(res.seña);
                  const total = parseCurrency(res.amount);
                  const diferencia = total - seña;
                  const bg = getBackgroundColor(
                    res.estado,
                    diferencia,
                    res.seña
                  );

                  return (
                    <TableRow
                      key={idx}
                      sx={{
                        cursor: "pointer",
                        fontWeight: "bold",
                        fontFamily: "'Red Hat Display'",
                        "&:hover": {
                          backgroundColor: "#e0e0e0",
                        },
                      }}
                      onClick={() => handleClick(res)}
                    >
                      <TableCell>{res.fullName || "No especificado"}</TableCell>
                      <TableCell>{formatDate(res.startDate)}</TableCell>
                      <TableCell>{formatDate(res.endDate)}</TableCell>
                      <TableCell>
                        {getRoomNamesByReservationId(res.reservationId)}
                      </TableCell>
                      <TableCell>
                        {res.guests || res.numGuests || "-"}
                      </TableCell>
                      <TableCell>
                        {calculateNights(res.startDate, res.endDate)}
                      </TableCell>
                      <TableCell sx={{ backgroundColor: bg }}>
                        ${seña.toLocaleString()}
                      </TableCell>
                      <TableCell>${total.toLocaleString()}</TableCell>
                      <TableCell>${diferencia.toLocaleString()}</TableCell>
                      <TableCell>{res.origin || "N/A"}</TableCell>
                      <TableCell>
                        {res.commissionAPI.toLocaleString()}
                      </TableCell>
                      <TableCell>
                        ${Math.round(total * 0.03).toLocaleString()}
                      </TableCell>
                      <TableCell>{res.ingresoX || "N/A"}</TableCell>
                    </TableRow>
                  );
                })
            ) : (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  No hay reservas encontradas.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export default ReservationHistory;
